import {
  DateRangePicker,
  Icon,
  RouteIconButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiCheckCircle,
  mdiClose,
  mdiEye,
  mdiMinusCircle,
  mdiPlus,
} from "@mdi/js";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import dayjs, { type Dayjs } from "dayjs";
import { enqueueSnackbar } from "notistack";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  type DeliveryScheduling,
  DeliveryStatus,
  DeliveryStatusLabels,
  statusColors,
} from "../../../@types/services/DeliverySchedule";
import { DeliveryCalendar } from "../../../components/molecules/DeliveryCalendar";
import type { DeliveryCalendarRef } from "../../../components/molecules/DeliveryCalendar/DeliveryCalendarProps";
import { useAuth } from "../../../hooks/useAuth";
import {
  approveDeliverySchedule,
  cancelDeliverySchedule,
  cancelDeliveryScheduleByVendor,
  listDeliverySchedules,
} from "../../../services/deliverySchedule";
import { i18nextParseToDate } from "../../../utils/i18nextParseTo";

interface DeliverySchedulingListProps {
  onStatusChange?: () => void;
}

interface PaginationState {
  current_page: number;
  items_per_page: number;
  total_pages: number;
  total_items: number;
}

const DeliverySchedulingList: React.FC<DeliverySchedulingListProps> = ({
  onStatusChange,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<DeliveryScheduling[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    current_page: 1,
    items_per_page: 10,
    total_items: 0,
    total_pages: 1,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    company: "",
    endDate: null as Dayjs | null,
    purchaseOrder: "",
    startDate: null as Dayjs | null,
    status: DeliveryStatus.PENDING,
  });
  const [loadingActions, setLoadingActions] = useState<Record<string, string>>(
    {},
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelJustification, setCancelJustification] = useState("");
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  const calendarRef = useRef<DeliveryCalendarRef>(null);

  const isPastDate = useCallback((date: Date | string): boolean => {
    const today = dayjs().startOf("day");
    return dayjs(date).isBefore(today);
  }, []);

  const fetchDeliverySchedules = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const params: {
        status?: DeliveryStatus;
        start_date?: string;
        end_date?: string;
        page: number;
        items_per_page: number;
      } = {
        items_per_page: rowsPerPage,
        page: page + 1,
      };

      if (filters.status) params.status = filters.status;
      if (filters.startDate)
        params.start_date = filters.startDate.format("YYYY-MM-DD");
      if (filters.endDate)
        params.end_date = filters.endDate.format("YYYY-MM-DD");

      const resp = await listDeliverySchedules(params);

      setData(resp.results);
      setPagination({
        current_page: resp.pagination.current_page,
        items_per_page: resp.pagination.items_per_page,
        total_items: resp.total_items,
        total_pages: resp.pagination.total_pages,
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("deliveryScheduling.fetchError"), {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.status,
    filters.startDate,
    filters.endDate,
    page,
    rowsPerPage,
    t,
  ]);

  useEffect(() => {
    fetchDeliverySchedules();
  }, [fetchDeliverySchedules]);

  const handleSlotSelect = useCallback(
    (start: Date) => {
      const startTime = dayjs(start).format("YYYY-MM-DDTHH:mm:ss");
      navigate({
        search: { datetime: startTime },
        to: "/delivery_schedules",
      });
    },
    [navigate],
  );

  const handlers = useMemo(
    () => ({
      handleChangePage: (_event: unknown, newPage: number): void => {
        setPage(newPage);
      },

      handleChangeRowsPerPage: (
        event: React.ChangeEvent<HTMLInputElement>,
      ): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
      },

      handleClearFilters: (): void => {
        setFilters({
          company: "",
          endDate: null,
          purchaseOrder: "",
          startDate: null,
          status: DeliveryStatus.PENDING,
        });
        setPage(0);
      },

      handleDateRangeChange: (start: Dayjs | null, end: Dayjs | null): void => {
        setFilters((prev) => ({
          ...prev,
          endDate: end,
          startDate: start,
        }));
        setPage(0);
      },
      handleFilterChange: (
        field: string,
        value: string | Dayjs | null | DeliveryStatus,
      ): void => {
        setFilters((prev) => ({
          ...prev,
          [field]: value,
        }));
        setPage(0);
      },
    }),
    [],
  );

  const handleCalendarRefresh = useCallback(async () => {
    await fetchDeliverySchedules();
    await calendarRef.current?.refreshCalendarData();
  }, [fetchDeliverySchedules]);

  const handleApprove = async (id: string): Promise<void> => {
    try {
      setLoadingActions((prev) => ({ ...prev, [id]: "approve" }));
      await approveDeliverySchedule(id);
      enqueueSnackbar(t("deliveryScheduling.approveSuccess"), {
        variant: "success",
      });
      fetchDeliverySchedules();
      onStatusChange?.();
      await handleCalendarRefresh();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("deliveryScheduling.approveError"), {
        variant: "error",
      });
    } finally {
      setLoadingActions((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  const handleCancel = async (id: string): Promise<void> => {
    if (user?.roles_names.includes("Delivery Schedule - Manage Time Slot")) {
      setCancelTargetId(id);
      setIsCancelModalOpen(true);
    } else if (user?.roles_names.includes("Delivery Schedule - Cancel")) {
      try {
        setLoadingActions((prev) => ({ ...prev, [id]: "cancel" }));
        await cancelDeliveryScheduleByVendor(id);
        enqueueSnackbar(t("deliveryScheduling.cancelSuccess"), {
          variant: "success",
        });
        fetchDeliverySchedules();
        onStatusChange?.();
        await handleCalendarRefresh();
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t("deliveryScheduling.cancelError"), {
          variant: "error",
        });
      } finally {
        setLoadingActions((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      }
    }
  };

  const handleCancelConfirm = async (): Promise<void> => {
    if (!cancelTargetId) return;
    setLoadingActions((prev) => ({ ...prev, [cancelTargetId]: "cancel" }));
    try {
      await cancelDeliverySchedule(cancelTargetId, cancelJustification);
      enqueueSnackbar(t("deliveryScheduling.cancelSuccess"), {
        variant: "success",
      });
      setIsCancelModalOpen(false);
      setCancelJustification("");
      setCancelTargetId(null);
      fetchDeliverySchedules();
      onStatusChange?.();
      await handleCalendarRefresh();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("deliveryScheduling.cancelError"), {
        variant: "error",
      });
    } finally {
      setLoadingActions((prev) => {
        const newState = { ...prev };
        if (cancelTargetId) delete newState[cancelTargetId];
        return newState;
      });
    }
  };

  const handleCancelModalClose = (): void => {
    setIsCancelModalOpen(false);
    setCancelJustification("");
    setCancelTargetId(null);
  };

  const handleClearFilters = (): void => {
    setFilters({
      company: "",
      endDate: null,
      purchaseOrder: "",
      startDate: null,
      status: DeliveryStatus.PENDING,
    });
    setPage(0);
  };

  const filteredData = data?.length
    ? data.filter((item) => {
        const purchaseOrderValue = item?.purchase_order || "";
        const documentReferenceValue = item?.document_reference || "";
        const searchableValue = purchaseOrderValue || documentReferenceValue;

        const matchesPO = filters?.purchaseOrder
          ? searchableValue
              .toLowerCase()
              .includes(filters.purchaseOrder.toLowerCase())
          : true;

        const vendorValue = item?.vendor || "";
        const matchesCompany = filters?.company
          ? vendorValue.toLowerCase().includes(filters.company.toLowerCase())
          : true;

        return matchesPO && matchesCompany;
      })
    : [];

  const paginationCount =
    filters.purchaseOrder || filters.company
      ? filteredData.length
      : pagination.total_items;

  const statusSelectId = useId();

  const renderTableContent = (): React.ReactElement | React.ReactElement[] => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell align="center" colSpan={5}>
            {t("loading")}
          </TableCell>
        </TableRow>
      );
    }

    if (filteredData.length === 0) {
      return (
        <TableRow>
          <TableCell align="center" colSpan={5}>
            {t("deliveryScheduling.noSchedulesFound")}
          </TableCell>
        </TableRow>
      );
    }

    return filteredData.map((row) => (
      <TableRow key={row.id}>
        <TableCell>
          {row.purchase_order || row.document_reference || "-"}
        </TableCell>
        {user?.roles_names.includes("View All Vendors") && (
          <TableCell sx={{ display: { md: "table-cell", xs: "none" } }}>
            {row.vendor}
          </TableCell>
        )}
        <TableCell sx={{ display: { sm: "table-cell", xs: "none" } }}>
          {t("intlDateTime", i18nextParseToDate(row.delivery_date_time))}
        </TableCell>
        <TableCell sx={{ display: { sm: "table-cell", xs: "none" } }}>
          {/* TODO: Replace the hardcoded colors in the code with theme variables. */}
          <Chip
            label={t(DeliveryStatusLabels[row.status])}
            size="small"
            sx={{
              "& .MuiChip-label": {
                color: "#ffffff",
              },
              backgroundColor: statusColors[row.status]?.color || "#9E9E9E",
              color: "#ffffff",
              fontWeight: "bold",
            }}
          />
        </TableCell>
        <TableCell align="center">
          <Box
            sx={{
              display: "flex",
              gap: { sm: 1, xs: 0.5 },
              justifyContent: "center",
            }}
          >
            <RouteIconButtonLink
              disabled={!!loadingActions[row.id]}
              params={{ deliveryScheduleId: row.id }}
              size="small"
              title={t("view")}
              to="/delivery_schedules/$deliveryScheduleId"
            >
              <Icon icon={mdiEye} />
            </RouteIconButtonLink>

            {user?.roles_names.includes("Delivery Schedule - Approve") &&
              row.status === DeliveryStatus.PENDING &&
              !isPastDate(row.delivery_date_time) && (
                <IconButton
                  color="success"
                  disabled={!!loadingActions[row.id]}
                  onClick={() => handleApprove(row.id)}
                  size="small"
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: { sm: "1.5rem", xs: "1.2rem" },
                    },
                    padding: { sm: 1, xs: 0.5 },
                  }}
                  title={t("calendar.approve")}
                >
                  {loadingActions[row.id] === "approve" ? (
                    <CircularProgress color="success" size={20} />
                  ) : (
                    <Icon icon={mdiCheckCircle} />
                  )}
                </IconButton>
              )}

            {(row.status === DeliveryStatus.PENDING ||
              row.status === DeliveryStatus.APPROVED) &&
              !isPastDate(row.delivery_date_time) && (
                <IconButton
                  color="error"
                  disabled={!!loadingActions[row.id]}
                  onClick={() => handleCancel(row.id)}
                  size="small"
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: { sm: "1.5rem", xs: "1.2rem" },
                    },
                    padding: { sm: 1, xs: 0.5 },
                  }}
                  title={t("cancel")}
                >
                  {loadingActions[row.id] === "cancel" ? (
                    <CircularProgress color="error" size={20} />
                  ) : (
                    <Icon icon={mdiMinusCircle} />
                  )}
                </IconButton>
              )}
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box
      sx={{
        maxWidth: "100%",
        p: { md: 3, sm: 2, xs: 1 },
        width: "100%",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: { sm: "row", xs: "column" },
          gap: 2,
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {user?.roles_names.includes("View All Vendors")
            ? t("deliveryScheduling.managementView")
            : t("deliveryScheduling.vendorView")}
        </Typography>

        {user?.roles_names.includes("Delivery Schedule - Create") && (
          <Button
            onClick={() => navigate({ to: "/delivery_schedules/new" })}
            startIcon={<Icon icon={mdiPlus} />}
            sx={{
              alignSelf: { sm: "auto", xs: "stretch" },
              minWidth: { sm: "auto", xs: "100%" },
            }}
            variant="contained"
          >
            {t("deliveryScheduling.newSchedule")}
          </Button>
        )}
      </Box>

      {user?.roles_names.includes("Delivery Schedule - Manage Time Slot") && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { lg: "row", xs: "column" },
            gap: 3,
            mb: 3,
          }}
        >
          <Box sx={{ flex: { lg: "0 0 78%", xs: "1" } }}>
            <DeliveryCalendar
              events={[]}
              onScheduleChange={fetchDeliverySchedules}
              onSlotSelect={handleSlotSelect}
              ref={calendarRef}
            />
          </Box>
          <Paper
            sx={{
              flex: { lg: "0 0 20%", xs: "1" },
              height: "fit-content",
              p: 2,
            }}
          >
            <Typography gutterBottom variant="h6">
              {t("deliveryScheduling.calendarGuide")}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom variant="subtitle1">
                {t("deliveryScheduling.statusGuide")}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                  <Box
                    sx={{
                      bgcolor: statusColors[DeliveryStatus.PENDING].color,
                      borderRadius: 1,
                      height: 20,
                      width: 20,
                    }}
                  />
                  <Typography variant="body2">
                    {t(DeliveryStatusLabels[DeliveryStatus.PENDING])}
                  </Typography>
                </Box>
                <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                  <Box
                    sx={{
                      bgcolor: statusColors[DeliveryStatus.APPROVED].color,
                      borderRadius: 1,
                      height: 20,
                      width: 20,
                    }}
                  />
                  <Typography variant="body2">
                    {t(DeliveryStatusLabels[DeliveryStatus.APPROVED])}
                  </Typography>
                </Box>
                <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                  <Box
                    sx={{
                      bgcolor: statusColors[DeliveryStatus.COMPLETED].color,
                      borderRadius: 1,
                      height: 20,
                      width: 20,
                    }}
                  />
                  <Typography variant="body2">
                    {t(DeliveryStatusLabels[DeliveryStatus.COMPLETED])}
                  </Typography>
                </Box>
                <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                  <Box
                    sx={{
                      bgcolor: "#ffe0e0",
                      border: "1px dashed #d32f2f",
                      borderRadius: 1,
                      height: 20,
                      width: 20,
                    }}
                  />
                  <Typography variant="body2">
                    {t("deliveryScheduling.disabled_hour")}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography component="p" sx={{ mb: 2 }} variant="body2">
                {t("deliveryScheduling.calendarInstructions.blockTimeSlot")}
              </Typography>
              <Typography component="p" sx={{ mb: 2 }} variant="body2">
                {t("deliveryScheduling.calendarInstructions.blockAllDay")}
              </Typography>
              <Typography component="p" sx={{ mb: 2 }} variant="body2">
                {t("deliveryScheduling.calendarInstructions.hoverInfo")}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      <Paper sx={{ mb: 2, overflowX: "hidden", p: 2 }}>
        <Box
          sx={{
            alignItems: { sm: "center", xs: "stretch" },
            display: "flex",
            flexDirection: { sm: "row", xs: "column" },
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h6">
            {t("deliveryScheduling.filters")}
          </Typography>
          <Button
            onClick={handleClearFilters}
            size="small"
            startIcon={<Icon icon={mdiClose} />}
            sx={{ alignSelf: { sm: "auto", xs: "flex-start" } }}
          >
            {t("deliveryScheduling.clearFilters")}
          </Button>
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              lg: "repeat(4, 1fr)",
              md: "repeat(3, 1fr)",
              sm: "1fr",
              xs: "1fr",
            },
          }}
        >
          <TextField
            fullWidth={false}
            label={`${t("deliveryScheduling.purchaseOrderOrDocumentReference")}`}
            onChange={(e) =>
              handlers.handleFilterChange("purchaseOrder", e.target.value)
            }
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{
              gridColumn: { md: "auto", xs: "1 / -1" },
              width: { md: "auto", xs: "100%" },
            }}
            value={filters.purchaseOrder}
          />
          {user?.roles_names.includes("View All Vendors") && (
            <TextField
              fullWidth
              label={t("quotation.vendor")}
              onChange={(e) =>
                handlers.handleFilterChange("company", e.target.value)
              }
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{
                gridColumn: { md: "auto", xs: "1 / -1" },
                width: { md: "auto", xs: "100%" },
              }}
              value={filters.company}
            />
          )}
          <FormControl
            fullWidth={false}
            size="small"
            sx={{
              gridColumn: { md: "auto", xs: "1 / -1" },
              width: { md: "auto", xs: "100%" },
            }}
          >
            <InputLabel id={statusSelectId} shrink>
              {t("deliveryScheduling.status.label")}
            </InputLabel>
            <Select
              displayEmpty
              label={t("deliveryScheduling.status.label")}
              labelId={statusSelectId}
              notched
              onChange={(e) =>
                handlers.handleFilterChange(
                  "status",
                  e.target.value as "" | DeliveryStatus,
                )
              }
              value={filters.status}
            >
              <MenuItem value="">{t("deliveryScheduling.status.all")}</MenuItem>
              <MenuItem value={DeliveryStatus.PENDING}>
                {t(DeliveryStatusLabels[DeliveryStatus.PENDING])}
              </MenuItem>
              <MenuItem value={DeliveryStatus.APPROVED}>
                {t(DeliveryStatusLabels[DeliveryStatus.APPROVED])}
              </MenuItem>
              <MenuItem value={DeliveryStatus.CANCELED}>
                {t(DeliveryStatusLabels[DeliveryStatus.CANCELED])}
              </MenuItem>
              <MenuItem value={DeliveryStatus.COMPLETED}>
                {t(DeliveryStatusLabels[DeliveryStatus.COMPLETED])}
              </MenuItem>
            </Select>
          </FormControl>
          <DateRangePicker
            endDate={filters.endDate}
            fullWidth={false}
            label={t("deliveryScheduling.dateRange")}
            onChange={handlers.handleDateRangeChange}
            size="small"
            startDate={filters.startDate}
            sx={{
              gridColumn: { md: "auto", xs: "1 / -1" },
              width: { md: "auto", xs: "100%" },
            }}
          />
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          boxSizing: "border-box",
          overflowX: "auto",
          width: "100%",
        }}
      >
        <Table
          sx={{
            minWidth: { sm: 600, xs: 300 },
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                {t("deliveryScheduling.purchaseOrderOrDocumentReference")}
              </TableCell>
              {user?.roles_names.includes("View All Vendors") && (
                <TableCell sx={{ display: { md: "table-cell", xs: "none" } }}>
                  {t("quotation.vendor")}
                </TableCell>
              )}
              <TableCell sx={{ display: { sm: "table-cell", xs: "none" } }}>
                {t("deliveryScheduling.deliveryDateTime")}
              </TableCell>
              <TableCell sx={{ display: { sm: "table-cell", xs: "none" } }}>
                {t("deliveryScheduling.status.label")}
              </TableCell>
              <TableCell align="center">
                {t("assignConditions.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableContent()}</TableBody>
        </Table>
        <TablePagination
          component="div"
          count={paginationCount}
          onPageChange={handlers.handleChangePage}
          onRowsPerPageChange={handlers.handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                display: { sm: "block", xs: "none" },
              },
          }}
        />
      </TableContainer>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={handleCancelModalClose}
        open={isCancelModalOpen}
      >
        <DialogTitle>{t("calendar.cancelSchedule")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            helperText={`${cancelJustification.length}/1000 caracteres`}
            label={t("calendar.cancelJustification")}
            margin="dense"
            multiline
            onChange={(e) => setCancelJustification(e.target.value)}
            rows={4}
            slotProps={{
              input: {
                inputProps: {
                  maxLength: 1000,
                },
              },
            }}
            sx={{ mt: 2 }}
            value={cancelJustification}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelModalClose}>{t("common.cancel")}</Button>
          <Button
            color="error"
            disabled={
              !cancelJustification.trim() ||
              !!(cancelTargetId && loadingActions[cancelTargetId] === "cancel")
            }
            onClick={handleCancelConfirm}
            startIcon={
              cancelTargetId && loadingActions[cancelTargetId] === "cancel" ? (
                <CircularProgress color="inherit" size={20} />
              ) : undefined
            }
            variant="contained"
          >
            {t("calendar.confirmCancellation")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
export const Route = createFileRoute("/_private/delivery_schedules/")({
  validateSearch: z.object({
    datetime: z.optional(z.string()).catch(""),
  }),
  component: DeliverySchedulingList,
  beforeLoad: ({ context }) => {
    const { auth } = context;
    if (!auth.user?.roles_names.includes("Delivery Schedule - View")) {
      throw redirect({ to: "/forbidden" });
    }
  },
});
